import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { foundation } from '../../../ui';
import { bg } from '../../../ui/theme/semanticTokens';
import * as p from '../../../ui/primitives';
import { totpQrImageSrc } from '../../../utils/auth/totpFactors';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import useMfaHandoff from './useMfaHandoff';
import MfaNoAppNote from './MfaNoAppNote';
import MfaSetupKeyBox from './MfaSetupKeyBox';
import { mfaStepTitleStyle } from './mfaStyles';

const f = foundation;
const fc = f.color;

const titleStyle = { ...mfaStepTitleStyle, margin: `0 0 ${f.spacePx[5]}` };
const primaryButtonStyle = (disabled) => ({ ...p.buttonPrimary, width: '100%', minHeight: 44, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: f.spacePx[2], textDecoration: 'none', opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer', boxSizing: 'border-box' });
const noteStyle = { fontSize: f.fontSize.xs, color: fc.neutral.textMuted, fontFamily: f.fontFamily.sans, textAlign: 'center', margin: `${f.spacePx[2]} 0 0` };
const dividerRow = { display: 'flex', alignItems: 'center', gap: f.spacePx[2], margin: `${f.spacePx[4]} 0 ${f.spacePx[3]}` };
const dividerLine = { flex: 1, height: 1, backgroundColor: fc.neutral.border };
const dividerText = { fontSize: f.fontSize.xs, color: fc.neutral.textMuted, fontFamily: f.fontFamily.sans, whiteSpace: 'nowrap' };
const discStyle = { border: `1px solid ${fc.neutral.border}`, borderRadius: f.radii.md, overflow: 'hidden', marginTop: f.spacePx[4] };
const discButtonStyle = { width: '100%', border: 'none', background: bg.surface, display: 'flex', alignItems: 'center', gap: f.spacePx[2], padding: `${f.spacePx[3]} ${f.spacePx[3]}`, minHeight: 44, font: 'inherit', fontFamily: f.fontFamily.sans, fontWeight: f.fontWeight.semibold, fontSize: f.fontSize.sm, color: fc.neutral.text, cursor: 'pointer', textAlign: 'left' };
const qrBoxStyle = { width: 168, height: 168, borderRadius: f.radii.md, border: `1px solid ${fc.neutral.border}`, display: 'block' };

/**
 * RD-SEC-015 flow A step 1 — "Añade CrewVex a tu app". Layout adapts by
 * device: touch (phone/tablet) leads with the real `<a href={payload.uri}>`
 * open-in-app link (flow G handoff) since you can't scan your own screen;
 * web leads with the QR. Both surface the setup key, masked until "Mostrar".
 *
 * RD-SEC-015 (2026-09-25 anatomy fix): the "I added it" CTA now lives in the
 * caller's PINNED footer, not inline here — `busy`/`onContinue` moved with
 * it. `title` is optional (default = the enrollment copy); change-phone
 * (`MfaChangePhoneModal`) overrides it with `mfaStepper.move.addTitle` since
 * it's adding a NEW app, not the first one.
 */
export default function MfaStepAdd({ compact, payload, title, onHandoffReturn }) {
  const intl = useIntl();
  const reducedMotion = useReducedMotion();
  const [showQr, setShowQr] = useState(false);
  const { startHandoff, noAppDetected } = useMfaHandoff({ onReturn: onHandoffReturn });
  const chevronTransition = reducedMotion ? 'none' : 'transform 200ms ease';

  if (!payload) return null;

  const keyBox = <MfaSetupKeyBox secret={payload.secret} compact={compact} />;

  const qrImg = payload.qr_code ? (
    <img alt={intl.formatMessage({ id: 'twoFactor.qrAlt', defaultMessage: 'QR code for authenticator setup' })} src={totpQrImageSrc(payload.qr_code)} style={qrBoxStyle} />
  ) : null;

  return (
    <div>
      <p style={titleStyle}>{title ?? intl.formatMessage({ id: 'mfaStepper.add.title', defaultMessage: 'Add CrewVex to your app' })}</p>

      {compact ? (
        <>
          <a href={payload.uri} style={primaryButtonStyle(false)} onClick={() => startHandoff()}>
            <ExternalLink size={16} aria-hidden />
            {intl.formatMessage({ id: 'mfaStepper.add.open', defaultMessage: 'Open in my app' })}
          </a>
          <p style={noteStyle}>{intl.formatMessage({ id: 'mfaStepper.add.iosHint', defaultMessage: 'On iPhone, pick your CrewVex password when asked.' })}</p>
          {noAppDetected ? <MfaNoAppNote compact={compact} /> : null}
          <div style={dividerRow}>
            <span style={dividerLine} /><span style={dividerText}>{intl.formatMessage({ id: 'mfaStepper.add.orKey', defaultMessage: 'or type the key' })}</span><span style={dividerLine} />
          </div>
          {keyBox}
          <div style={discStyle}>
            {/* Touch: the QR is the fallback (you can't scan your own
                screen), so this disclosure reveals it — "Show QR code".
                Web leads with the QR and its disclosure is "Can't scan?". */}
            <button type="button" style={discButtonStyle} onClick={() => setShowQr((v) => !v)} aria-expanded={showQr} data-testid="mfa-add-disclosure">
              <span style={{ flex: 1 }}>{intl.formatMessage({ id: 'mfaStepper.add.showQr', defaultMessage: 'Show QR code' })}</span>
              <ChevronRight size={16} aria-hidden style={{ transform: showQr ? 'rotate(90deg)' : 'none', transition: chevronTransition, color: fc.neutral.iconMuted }} />
            </button>
            {showQr && qrImg ? (
              <div style={{ padding: `0 ${f.spacePx[3]} ${f.spacePx[3]}`, display: 'flex', justifyContent: 'center' }}>{qrImg}</div>
            ) : null}
          </div>
        </>
      ) : (
        <>
          {qrImg ? <div style={{ display: 'flex', justifyContent: 'center', marginBottom: f.spacePx[4] }}>{qrImg}</div> : null}
          <div style={discStyle}>
            <button type="button" style={discButtonStyle} onClick={() => setShowQr((v) => !v)} aria-expanded={showQr} data-testid="mfa-add-disclosure">
              <span style={{ flex: 1 }}>{intl.formatMessage({ id: 'mfaStepper.add.cantScan', defaultMessage: 'Can’t scan?' })}</span>
              <ChevronRight size={16} aria-hidden style={{ transform: showQr ? 'rotate(90deg)' : 'none', transition: chevronTransition, color: fc.neutral.iconMuted }} />
            </button>
            {showQr ? <div style={{ padding: `0 ${f.spacePx[3]} ${f.spacePx[3]}` }}>{keyBox}</div> : null}
          </div>
        </>
      )}
    </div>
  );
}
